import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { mdiAccountOutline, mdiShieldCheck } from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import profileBackground from "assets/images/profile-bg.svg";
import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import CustomMap from "components/CustomMap";
import Header from "components/Header";
import Rater from "components/Rater";
import RateUserModal from "components/RateUserModal";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import MainLayout from "layouts/Main";
import { IOtherUserProfile, IRatingData, IReviewData } from "models/User";
import { getAccountAPI } from "services/api/user";
import { getPreviousRatingAPI, postBlockUsersAPI } from "services/api/userSetting";
import { AppDispatch, RootState } from "store";
import { handleLoading } from "store/global/loader";
import styles from "./style.module.scss";

export default function OtherUserProfile() {
  const dispatch = useDispatch<AppDispatch>();
  const [isRateUserModalOpen, toggleRateUserModal] = useToggle();
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle();
  const [rating, setRating] = useState<IRatingData>({
    rating: 0,
    comment: ""
  });
  const [previousRatings, setPreviousRatings] = useState<IReviewData[]>([]);
  const [userProfile, setUserProfile] = useState<IOtherUserProfile>();
  const [isRatedUser, setIsRatedUser] = useState(false);
  const { account } = useSelector((state: RootState) => state.authState);
  const { userId } = useParams();

  const currentRating = useMemo(() => {
    const ratingsSum = previousRatings.reduce((prev, cur) => prev + cur.rating, 0);
    return Math.round(ratingsSum / (previousRatings.length * 20));
  }, [previousRatings]);

  const fetchAccountDetails = async () => {
    if (userId) {
      handleLoading(true);
      const { data } = await getAccountAPI(userId);
      handleLoading(false);
      setUserProfile(data);
    }
  };

  const fetchPreviousRating = async () => {
    if (userId) {
      handleLoading(true);
      const { data } = await getPreviousRatingAPI(userId);
      handleLoading(false);
      setPreviousRatings(data.results);
    }
  };

  const { contact_name, dba, contact_phone, email, address, city, state, zip, country } = {
    ...userProfile
  };

  const addressInfo = [address, city, state, zip, country]
    .filter((item) => !!item && item !== "undefined")
    .join(", ");

  const editMyReview = (item: IReviewData) => {
    setRating(item);
    toggleRateUserModal();
  };

  const blockUser = async () => {
    if (userId) {
      dispatch(handleLoading(true));
      await postBlockUsersAPI(userId)
        .then(() => {
          showSuccessMessage("Successfully blocked user.");
          toggleConfirmModal();
          window.location.reload();
        })
        .catch(() => {
          showErrorMessage("Failed to block user.");
        })
        .finally(() => dispatch(handleLoading(false)));
    }
  };

  useEffect(() => {
    fetchAccountDetails();
    fetchPreviousRating();
  }, []);

  return (
    <MainLayout
      desktopTitle=""
      customHeader={<Header label="" />}
      className={styles.userProfileContainer}>
      <div className={styles.profileBase}>
        <div className="relative w-full">
          <img
            src={profileBackground}
            alt="profile background"
            className={styles.profileBackground}
          />
          <div className={styles.userAvatar}>
            <Icon path={mdiAccountOutline} size={3.5} className="m-auto text-text" />
          </div>
        </div>

        <div className={styles.ratingStatus}>
          <span className="text-xl font-bold">{dba}</span>
          <Rater value={currentRating} readOnly />
        </div>

        {userId !== account?.id ? (
          <div className={styles.ratingAction}>
            <Button type="button" variant="outline" onClick={toggleConfirmModal}>
              Block User
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (previousRatings.find((rate) => rate.to_user_id === userId)) {
                  setIsRatedUser(true);
                } else {
                  setIsRatedUser(false);
                }
                toggleRateUserModal();
              }}>
              Rate User
            </Button>
          </div>
        ) : null}
        <div className={styles.verificationStatus}>
          <Icon path={mdiShieldCheck} className="size-6" />
          <span className="text-sm leading-6">Haul Pay Verified Carrier</span>
        </div>
      </div>

      <ul className={styles.detailInfo}>
        <li>
          <span>Contact:</span>
          <span>{contact_name}</span>
        </li>
        <li>
          <span>Title:</span>
          <span>{dba}</span>
        </li>
        <li>
          <span>Authority Document:</span>
          <Link to="" className="underline">
            View Document
          </Link>
        </li>
        <li>
          <span>Insurance Document:</span>
          <Link to="" className="underline">
            View Document
          </Link>
        </li>
        <li>
          <span>Contact Number:</span>
          <a href={`tel:${contact_phone}`} className="underline">
            {contact_phone}
          </a>
        </li>
        <li>
          <span>Email id:</span>
          <a href={`mailto:${email}`} className="underline">
            {email}
          </a>
        </li>
        <li>
          <span>Address:</span>
          <span>{addressInfo}</span>
        </li>
      </ul>

      <div className={styles.userProfileMap}>
        <span className="pl-1 font-medium">Map</span>

        <CustomMap points={[]} />
      </div>

      {previousRatings?.map((item) => (
        <ul key={item.id} className={styles.detailInfo}>
          <span className="-ml-1 text-base font-medium">Review Comments</span>
          <li>
            <span>Contact:</span>
            <Rater value={item.rating / 20} disabled />
          </li>
          <li>
            <span>Create at</span>
            <span>{dayjs(item.time_posted).format("MM/DD/YYYY HH:mm:ss A")}</span>
          </li>
          <li>
            <span>Comment</span>
            <span>{item.comment}</span>
          </li>
          {item.from_user_id === account.id && (
            <Button className="self-end" onClick={() => editMyReview(item)}>
              Edit My Review
            </Button>
          )}
        </ul>
      ))}

      <ConfirmationModal
        isConfirmModalOpen={isConfirmModalOpen}
        toggleConfirmModal={toggleConfirmModal}
        onOK={blockUser}
        content="Are you sure you want to block all posts by this user? You can manage blocked users from Settings under the Account section."
      />

      <RateUserModal
        isRateUserModalOpen={isRateUserModalOpen}
        toggleRateUserModal={toggleRateUserModal}
        isRatedUser={isRatedUser}
        setIsRatedUser={setIsRatedUser}
        userId={userId ?? ""}
        rating={rating}
        setRating={setRating}
        previousRatings={previousRatings}
        setPreviousRatings={setPreviousRatings}
        fetchPreviousRating={fetchPreviousRating}
      />
    </MainLayout>
  );
}
